import { Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';
import Card from '../../CaseManagement/components/Card';
import PinResetCard from './cards/PinResetCard';
import AuthLinkCard from './cards/AuthLinkCard';
import AdminMemberGPFundDeposit from './AdminMemberGPFundDeposit';
import useMemberUnassignDebit from '../hooks/useMemberUnassignDebit';
import useMemberSyncDebitBalance from '../hooks/useMemberSyncDebitBalance';
import useAdminMemberWebPortalLinks from '../hooks/useAdminMemberWebPortalLinks';
import { Homeless } from 'types';

const useStyles = makeStyles(() => ({
    section: {
        marginBottom: '0px'
    },
    whiteContainer: {
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 8px 16px 4px rgba(0, 0, 0, 0.05)',
        borderRadius: '16px',
        padding: '32px',
        marginTop: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
    },
    actionButton: {
        width: '100%',
        padding: '16px',
        textTransform: 'none',
        fontFamily: 'Manrope',
        fontSize: '16px',
        fontWeight: 400,
        backgroundColor: '#FFFFFF',
        color: '#1F2152',
        border: '1.5px solid #E3E3FA',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            backgroundColor: '#7378E810',
            borderColor: '#7378E8'
        },
        '&.completed': {
            backgroundColor: '#F6F7FF',
            borderColor: '#7378E8',
            color: '#7378E8'
        },
        '&.Mui-disabled': {
            opacity: 0.2,
            color: '#1F2152'
        }
    },
    buttonIcon: {
        fontSize: '16px',
        fontWeight: 500,
        color: '#7378E8'
    }
}));

type Props = {
    homeless: Homeless;
};

const AdminActions: React.FC<Props> = ({ homeless }) => {
    const classes = useStyles();
    const hasCard = Boolean(homeless.card?.account_id);
    
    // Calculate if we can send email (when contact info exists)
    const canSendEmail = Boolean(homeless.email || homeless.phone_number);

    // States for showing cards
    const [showPinCard, setShowPinCard] = useState(false);
    const [showAuthCard, setShowAuthCard] = useState(false);
    const [showAddFundsModal, setShowAddFundsModal] = useState(false);

    // Admin actions hooks
    const unassignCard = useMemberUnassignDebit(homeless);
    const syncBalance = useMemberSyncDebitBalance(homeless);
    const webPortalLinks = useAdminMemberWebPortalLinks(homeless);

    const handlePinReset = async () => {
        const success = await webPortalLinks.pinReset.generate();
        if (success) {
            setShowPinCard(true);
        }
    };

    const handleAuthLink = async () => {
        const success = await webPortalLinks.authLink.generate();
        if (success) {
            setShowAuthCard(true);
        }
    };

    const handleAddFunds = () => {
        setShowAddFundsModal(true);
    };

    return (
        <div className={classes.section}>
            <Card
                info
                adminActions
                name="Admin Actions"
                count={0}
            />
            <div className={classes.whiteContainer}>
                {/* PIN Reset - Button or Card */}
                {!showPinCard ? (
                    <Button
                        className={classes.actionButton}
                        onClick={handlePinReset}
                        disabled={webPortalLinks.pinReset.isLoading}
                    >
                        <span>Generate PIN Reset Link</span>
                        <i className={`far ${webPortalLinks.pinReset.isLoading ? 'fa-spinner fa-spin' : 'fa-user-lock'} ${classes.buttonIcon}`} />
                    </Button>
                ) : (
                    <PinResetCard 
                        onClose={() => setShowPinCard(false)}
                        pinResetLink={webPortalLinks.pinReset.link}
                        sendEmail={webPortalLinks.pinReset.sendEmail}
                        canSendEmail={canSendEmail}
                    />
                )}

                {/* Auth Link - Button or Card */}
                {!showAuthCard ? (
                    <Button
                        className={classes.actionButton}
                        onClick={handleAuthLink}
                        disabled={webPortalLinks.authLink.isLoading}
                    >
                        <span>Generate Auth Link For Member Portal</span>
                        <i className={`far ${webPortalLinks.authLink.isLoading ? 'fa-spinner fa-spin' : 'fa-key'} ${classes.buttonIcon}`} />
                    </Button>
                ) : (
                    <AuthLinkCard 
                        onClose={() => setShowAuthCard(false)}
                        authLink={webPortalLinks.authLink.link}
                        sendEmail={webPortalLinks.authLink.sendEmail}
                        canSendEmail={canSendEmail}
                    />
                )}

                {/* Sync Balance - Using hook */}
                <Button
                    className={`${classes.actionButton} ${syncBalance.isComplete ? 'completed' : ''}`}
                    onClick={syncBalance.execute}
                    disabled={syncBalance.isLoading || !hasCard}
                >
                    <span>
                        {syncBalance.isLoading ? 'Syncing Balance...' : 
                         syncBalance.isComplete ? 'Balance Synced Successfully' :
                         'Sync Debit Card Balance from GiveCard/PEX'}
                    </span>
                    <i className={`far ${
                        syncBalance.isLoading ? 'fa-spinner fa-spin' : 
                        syncBalance.isComplete ? 'fa-check' : 
                        'fa-sync'
                    } ${classes.buttonIcon}`} />
                </Button>

                {/* Unassign Card - Using hook */}
                <Button
                    className={`${classes.actionButton} ${unassignCard.isComplete ? 'completed' : ''}`}
                    onClick={unassignCard.execute}
                    disabled={unassignCard.isLoading || !hasCard}
                >
                    <span>
                        {unassignCard.isLoading ? 'Unassigning Card...' :
                         unassignCard.isComplete ? 'Card Unassigned Successfully' :
                         'Unassign Debit Card'}
                    </span>
                    <i className={`far ${
                        unassignCard.isLoading ? 'fa-spinner fa-spin' :
                        unassignCard.isComplete ? 'fa-check' :
                        'fa-credit-card'
                    } ${classes.buttonIcon}`} />
                </Button>

                {/* Add Funds - Just Button */}
                <Button
                    className={classes.actionButton}
                    onClick={handleAddFunds}
                >
                    <span>Add Funds from General Pool</span>
                    <i className={`far fa-hand-holding-usd ${classes.buttonIcon}`} />
                </Button>
            </div>

            {/* Add Funds Modal */}
            <AdminMemberGPFundDeposit
                isOpen={showAddFundsModal}
                onClose={() => setShowAddFundsModal(false)}
                homeless={homeless}
            />
        </div>
    );
};

export default AdminActions; 